import React from "react";
import { camelCase } from "es-toolkit";
import { getStyle } from "@vibe/shared";
import { ComponentDefaultTestId, getTestId } from "../../tests/test-ids-utils";
import cx from "classnames";
import { Icon, CustomSvgIcon } from "@vibe/icon";
import { type AvatarSize } from "./Avatar.types";
import styles from "./AvatarBadge.module.scss";
import { type VibeComponentProps } from "../../types";
import { type SubIcon } from "@vibe/icon";
import { ClickableWrapper } from "@vibe/clickable";

export interface AvatarBadgeProps extends VibeComponentProps {
  /**
   * The image source for the badge.
   */
  src?: string;
  /**
   * The icon displayed inside the badge.
   */
  icon?: SubIcon;
  /**
   * The tab index of the badge.
   */
  tabIndex?: number;
  /**
   * The size of the badge.
   */
  size?: AvatarSize;
  /**
   * Callback fired when the badge is clicked.
   */
  onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
}

const AvatarBadge = ({
  src,
  icon,
  tabIndex = 0,
  className,
  size = "large",
  id,
  onClick,
  "data-testid": dataTestId,
  ...otherProps
}: AvatarBadgeProps) => {
  const classNames = cx(getStyle(styles, camelCase("badge--" + size)), className);
  const testId = dataTestId || getTestId(ComponentDefaultTestId.AVATAR_BADGE, id);
  const isClickable = tabIndex === -1 && !!onClick;

  if (icon) {
    return isClickable ? (
      <ClickableWrapper isClickable clickableProps={{ onClick, tabIndex }}>
        <Icon icon={icon} className={classNames} {...otherProps} data-testid={testId} />
      </ClickableWrapper>
    ) : (
      <Icon icon={icon} className={classNames} tabindex={tabIndex} {...otherProps} data-testid={testId} />
    );
  }

  const svgIcon = <CustomSvgIcon src={src} className={classNames} {...otherProps} data-testid={testId} />;

  return src ? (
    isClickable ? (
      <ClickableWrapper isClickable clickableProps={{ onClick, tabIndex }}>
        {svgIcon}
      </ClickableWrapper>
    ) : (
      svgIcon
    )
  ) : null;
};

export default AvatarBadge;
